<template>
    <div class="">
        <rooster-header title="Tracker">
            <select class="form-control"
                    v-if="projects.length" v-model="currentProjectId" @change="onProjectChange"
                    :disabled="!canChangeProject"
                    :title="!canChangeProject ? 'You can not change while you are clocked in.' : ''"
            >
                <option v-for="project in projects" :value="project.id">{{project.name}}
                    <span v-if="project.client">[{{project.client.name}}]</span></option>
            </select>
        </rooster-header>

        <template v-if="!loaded" >
            <p class="alert alert-info"><i class="fas fa-spinner fa-spin"></i> Just a second..</p>
        </template>

        <template v-if="loaded && !currentProjectId" >
            <p class="alert alert-warning">
                <i class="fas fa-hand-paper"></i>
                You haven&#039;t created any project.
                <router-link to="/projects">Create one</router-link>.
            </p>
        </template>

        <template v-if="loaded && !timezoneSet" >
            <p class="alert alert-info">
                <i class="fas fa-user-clock"></i>
                Your timezone is now implied as UTC. When you care to change it, do so
                <router-link to="/settings">here</router-link>.
            </p>
        </template>

        <template v-if="isReadyForPunching">
            <div :class="clockStateClass" role="alert">
                <div class="alert-heading">
                    <span class="float-right">{{timeSinceLastEvent}}</span>
                    <h5 class="mb-0">
                        <!--<i class="fa fa-stop"></i>-->
                        {{clockState}} <span v-if="lastPunch">({{lastPunch.created_at}})</span>
                    </h5>
                </div>
            </div>

            <div class="form-group d-none d-md-flex">
                <div class="input-group">
                    <input class="form-control form-control-lg" type="text" :placeholder="placeholder"
                           v-model="note"
                           v-on:keyup.enter="onEnterKey"
                           :disabled="noteDisabled"
                    />
                    <div class="input-group-append">
                        <button class="btn btn-primary" type="button" @click="clockin" :disabled="!canClockIn">clock in</button>
                        <button class="btn btn-primary" type="button" @click="clockbreak" :disabled="!canBreak">break</button>
                        <button class="btn btn-primary" type="button" @click="clockout" :disabled="!canClockOut">clock out</button>
                    </div>
                </div>
            </div>

            <div class="form-group d-md-none">
                <input class="form-control form-control-lg" type="text" :placeholder="placeholder"
                       v-model="note"
                       :disabled="noteDisabled"
                />
            </div>

            <div class="form-group d-md-none">
                <div class="btn-group d-flex" role="group" aria-label="Basic example">
                    <button class="btn btn-success" type="button" @click="clockin" :disabled="!canClockIn">clock in</button>
                    <button class="btn btn-success" type="button" @click="clockbreak" :disabled="!canBreak">break</button>
                    <button class="btn btn-success" type="button" @click="clockout" :disabled="!canClockOut">clock out</button>
                </div>
            </div>

            <table v-if="latestActivity.length" class="table table-borderless table-sm mt-3">
                <tr v-for="p in latestActivity">
                    <td>{{p.created_at}}</td>
                    <td>{{p.type}}</td>
                    <td>{{p.note}}</td>
                </tr>
            </table>
        </template>
    </div>
</template>

<script>
    Vue.component('rooster-header', require('../Header').default);


    export default {
        name: "Tracker",

        props: [
            "now"
        ],

        data() {
            let now = new Date(this.now);

            return {
                user: null,
                projects: [],
                currentProjectId: null,
                lastPunch: null,
                lastNote: null,     // last entered note (last textual representation of the task)
                latestActivity: [],
                loaded: false,
                note: "",
                nowState: now.getTime()/1000,
                intervalId: null,
            }
        },

        watch: {
            currentProjectId: function (newId, oldId) {
                const that = this;
                window.axios.get('api/project/' + newId)
                    .then(function(resp){
                        that.latestActivity = resp.data.punches.slice(0, 10);
                        that.lastNote = resp.data.last_note;

                        //console.log(resp.data);

                        if (resp.data.punches.length) {
                            that.lastPunch = resp.data.punches[0];
                        } else {
                            that.lastPunch = null;
                        }
                    });
            }
        },

        mounted() {
            const that = this;

            window.axios.get('/api/user')
                .then(function(resp){
                    that.user = resp.data.user;
                    that.projects = resp.data.projects;

                    if (that.projects.length) {
                        that.currentProjectId = that.projects[0].id;
                    }

                    // update time
                    that.nowState = (new Date(resp.data.now).getTime())/1000;

                    that.loaded = true;
                    //console.log("Just set loaded to true.");
                }).catch(function(err, resp){
                    alert("Oops, there was an error fetching data form server.");
                    //console.log(err.message);
                });

            this.heartBeat()
        },

        computed: {
            timezoneSet() {
                //console.log(this.user && this.user.timezone !== null && this.user.timezone != "");
                return this.user && this.user.timezone !== null;
            },

            isReadyForPunching() {
                return this.currentProjectId;
            },

            clockStateClass() {
                if (!this.lastPunch) {
                    return "alert alert-secondary";
                }

                switch (this.lastPunch.type) {
                    case "clock in":
                        return "alert alert-success";
                    case "break":
                        return "alert alert-warning";
                    case "clock out":
                        return "alert alert-secondary";
                }
            },

            canClockIn() {
                return !this.lastPunch || this.lastPunch.type == 'clock out' || this.lastPunch.type == 'break';
            },

            canBreak() {
                if (!this.lastPunch) {
                    return false;
                }
                return this.lastPunch.type == 'clock in';
            },

            canClockOut() {
                if (!this.lastPunch) {
                    return false;
                }
                return this.lastPunch.type == 'clock in' || this.lastPunch.type == 'break';
            },

            clockState() {
                if (!this.lastPunch) {
                    return "never worked on";
                }

                switch (this.lastPunch.type) {
                    case "clock in":
                        return "clocked in";
                    case "break":
                        return "in break";
                    case "clock out":
                        return "clocked out";
                }
            },

            timeSinceLastEvent() {
                if (!this.lastPunch || this.lastPunch.type=='clock out') {
                    return "";
                }

                let lp = new Date(this.lastPunch.created_at);

                return this.niceTime(this.nowState - lp.getTime()/1000);
            },

            canChangeProject() {
                if (!this.lastPunch) {
                    return true;
                }

                return this.lastPunch.type != "clock in";
            },

            noteDisabled() {
                if (!this.lastPunch) {
                    return false;
                }

                return this.lastPunch.type != 'clock out';
            },

            placeholder() {
                let ln = this.lastNote ? this.lastNote : "new task?";
                if (this.lastPunch && this.lastPunch.type == "clock out") {
                    ln = "last worked: " + ln;
                }
                return ln;
            }
        },

        methods: {
            clockin() {
                const that = this;
                window.axios.post("/api/clock", {
                    "project_id": that.currentProjectId,
                    "action": "clock in",
                    "note": that.note
                }).then(function(resp){
                    that.lastPunch = resp.data.punch;
                    that.lastNote = resp.data.last_note;
                    that.latestActivity.unshift(that.lastPunch);
                    that.latestActivity.splice(10);
                    that.nowState = (new Date(resp.data.now).getTime())/1000;
                    that.note = "";
                    that.heartBeat();
                }).catch(function(error){
                    that.handleError(error.response.data);
                });
            },

            clockbreak() {
                const that = this;
                window.axios.post("/api/clock", {
                    "project_id": that.currentProjectId,
                    "action": "break"
                }).then(function(resp){
                    that.lastPunch = resp.data.punch;
                    that.latestActivity.unshift(that.lastPunch);
                    that.latestActivity.splice(10);
                    that.nowState = (new Date(resp.data.now).getTime())/1000;
                    that.note = "";
                    that.heartBeat();
                }).catch(function(error){
                    that.handleError(error.response.data);
                });
            },

            clockout() {
                const that = this;
                window.axios.post("/api/clock", {
                    "project_id": that.currentProjectId,
                    "action": "clock out"
                }).then(function(resp){
                    that.lastPunch = resp.data.punch;
                    that.latestActivity.unshift(that.lastPunch);
                    that.latestActivity.splice(10);
                    that.nowState = (new Date(resp.data.now).getTime())/1000;
                    that.note = "";
                    that.heartBeat();
                }).catch(function(error){
                    that.handleError(error.response.data);
                });
            },

            onProjectChange(e) {
                this.currentProjectId = e.target.value;
            },

            niceTime(seconds) {
                let hours, minutes;
                let string = "";
                let hourString = ""
                let minuteString = "";

                let s = seconds;
                hours = Math.floor(seconds/3600);
                s %= 3600;
                minutes = Math.floor(s/60);
                s %= 60;

                if (seconds<2) {
                    return "just now";
                }

                if (seconds<60){
                    return seconds + ' seconds';
                }

                if(hours > 0){
                    if(hours == 1) { hourString = 'hour'; } else { hourString  = 'hours';}
                    string = hours + ' ' + hourString;
                }
                if(minutes > 0){
                    if(minutes == 1) {
                        minuteString = 'minute';
                    } else {
                        minuteString = 'minutes';
                    }
                    if(string.length>0) string += ', ';
                    string += minutes + ' ' + minuteString;
                }

                return string;
            },

            heartBeat() {
                if (this.intervalId) {
                    window.clearInterval(this.intervalId);
                }

                const that = this;
                this.intervalId = window.setInterval(function(){
                    if (that.nowState) {
                        that.nowState += 5
                    }
                }, 5000)
            },

            handleError(data) {
                if (typeof data == "object") {
                    alert(data.message)
                } else {
                    alert(data);
                }
            },

            onEnterKey(e) {
                this.clockin()
            }
        }
    }
</script>

<style scoped>

</style>
